import { c as _c } from "react/compiler-runtime";
// ── MetricErrorState — compact error surface for a KPI/metric tile ──────────
// `StateDisplay` is a large centered block, wrong for a 68px KPI tile — this
// mirrors the real numeric-tile card's fixed h-17 (68px), see WidgetCard.tsx,
// so swapping the error state in causes no reflow. Figma: PSA / Key-Metric,
// node 1793:11651.
//
// No `icon`/`isBordered` props (unlike ErrorState) — the design has no icon,
// and the border is tile geometry, not a configurable variant. Ref: ASC-6756.

import type { ReactElement, ReactNode } from 'react';
import { Button } from '@oraczen/zendesign-ui/components/button';
import { RefreshCw } from 'lucide-react';
import { CopySupportIdIconButton } from './SupportIdRow';
export interface MetricErrorStateProps {
  /** Metric name when known; falls back to a generic title. */
  title?: string;
  /** Falls back to a generic no-data message. */
  description?: string;
  supportId?: string | null;
  /** Replaces the default RefreshCw icon-button entirely. */
  actions?: ReactNode;
  onRetry?: () => void;
  retrying?: boolean;
}
export function MetricErrorState(t0) {
  const $ = _c(22);
  const {
    title: t1,
    description: t2,
    supportId: t3,
    actions,
    onRetry,
    retrying: t4
  } = t0;
  const title = t1 === undefined ? "Metric" : t1;
  const description = t2 === undefined ? "No Data Found" : t2;
  const supportId = t3 === undefined ? null : t3;
  const retrying = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== onRetry || $[1] !== retrying) {
    t5 = function handleRetryClick() {
      if (retrying) {
        return;
      }
      onRetry?.();
    };
    $[0] = onRetry;
    $[1] = retrying;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const handleRetryClick = t5;
  let t6;
  if ($[3] !== actions || $[4] !== handleRetryClick || $[5] !== retrying) {
    t6 = actions ?? <Button type="button" variant="default" size="iconTiny" isCurved={true} aria-label="Retry" onClick={handleRetryClick} disabled={retrying} loading={retrying}><RefreshCw className="size-4" /></Button>;
    $[3] = actions;
    $[4] = handleRetryClick;
    $[5] = retrying;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const primaryAction = t6;
  let t7;
  if ($[7] !== title) {
    t7 = <span className="textExtraSmallLeadingNormalRegular text-foreground truncate">{title}</span>;
    $[7] = title;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== description) {
    t8 = <span className="textExtraSmallLeadingNormalSemibold text-muted-foreground truncate">{description}</span>;
    $[9] = description;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t7 || $[12] !== t8) {
    t9 = <div className="flex flex-1 flex-col justify-between gap-1">{t7}{t8}</div>;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== supportId) {
    t10 = supportId ? <CopySupportIdIconButton supportId={supportId} /> : null;
    $[14] = supportId;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== primaryAction || $[17] !== t10) {
    t11 = <div className="flex items-center gap-2">{primaryAction}{t10}</div>;
    $[16] = primaryAction;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t9) {
    t12 = <div role="alert" className="h-17 flex items-center gap-3 rounded-md border border-border bg-card p-3 shadow-none">{t9}{t11}</div>;
    $[19] = t11;
    $[20] = t9;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
}